class CartUpsells extends HTMLElement {
  connectedCallback() {
    // Abort previous listeners if element is reconnected (e.g. after DOM morph)
    this.#abortController?.abort();
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    // Use event delegation on the component root so listeners
    // survive child element replacement during DOM morphing
    this.addEventListener('click', (e) => {
      const tab = e.target.closest('.cart-upsells__tab');
      if (tab) {
        this.#handleTabClick(tab);
        return;
      }

      const addButton = e.target.closest('[data-add-to-cart]');
      if (addButton && !addButton.disabled) {
        e.preventDefault();
        const variantId = addButton.dataset.variantId;
        if (variantId) {
          this.#addToCart(variantId, addButton);
        }
      }
    }, { signal });

    this.addEventListener('change', (e) => {
      const select = e.target.closest('[data-option-select]');
      if (!select) return;

      const productCard = select.closest('.cart-upsells__product');
      if (productCard) {
        this.#handleOptionChange(productCard);
      }
    }, { signal });
  }

  disconnectedCallback() {
    this.#abortController?.abort();
  }

  #abortController = null;

  #handleTabClick(tab) {
    const tabIndex = tab.dataset.tabIndex;

    this.querySelectorAll('.cart-upsells__tab').forEach((t) => {
      t.classList.remove('cart-upsells__tab--active');
      t.setAttribute('aria-selected', 'false');
    });
    tab.classList.add('cart-upsells__tab--active');
    tab.setAttribute('aria-selected', 'true');

    this.querySelectorAll('.cart-upsells__panel').forEach((panel) => {
      panel.classList.remove('cart-upsells__panel--active');
      panel.hidden = true;
    });
    const activePanel = this.querySelector(`[data-panel-index="${tabIndex}"]`);
    if (activePanel) {
      activePanel.classList.add('cart-upsells__panel--active');
      activePanel.hidden = false;
    }
  }

  #handleOptionChange(productCard) {
    const selects = productCard.querySelectorAll('[data-option-select]');
    const variantsJson = productCard.querySelector('[data-variants-json]');

    if (!variantsJson) return;

    let variants;
    try {
      variants = JSON.parse(variantsJson.textContent);
    } catch (e) {
      console.error('Failed to parse variants JSON', e);
      return;
    }

    const selectedOptions = [];
    let allSelected = true;

    selects.forEach((select) => {
      const optionIndex = parseInt(select.dataset.optionIndex);
      const value = select.value;

      if (!value) {
        allSelected = false;
      }

      selectedOptions[optionIndex - 1] = value;

      const label = productCard.querySelector(`[data-selected-option="${optionIndex}"]`);
      if (label) {
        label.textContent = value || '-';
      }
    });

    const addButton = productCard.querySelector('[data-add-to-cart]');

    if (!allSelected) {
      if (addButton) {
        addButton.disabled = true;
      }
      return;
    }

    const matchingVariant = variants.find((variant) => {
      return variant.options.every((option, index) => {
        return String(option).trim() === String(selectedOptions[index]).trim();
      });
    });

    if (matchingVariant) {
      if (addButton) {
        addButton.dataset.variantId = matchingVariant.id;
        addButton.disabled = !matchingVariant.available;
        addButton.textContent = matchingVariant.available
          ? addButton.dataset.textAdd || 'Add'
          : addButton.dataset.textSoldOut || 'Sold out';
      }

      this.#updatePrices(productCard, matchingVariant.price, matchingVariant.compareAtPrice);
    } else {
      if (addButton) {
        addButton.disabled = true;
        addButton.textContent = addButton.dataset.textUnavailable || 'Unavailable';
      }
    }
  }

  #updatePrices(productCard, price, comparePrice) {
    const currentPriceEl = productCard.querySelector('[data-current-price]');
    const comparePriceEl = productCard.querySelector('[data-compare-price]');
    const discountEl = productCard.querySelector('[data-discount]');

    if (currentPriceEl && price) {
      currentPriceEl.textContent = this.#formatMoney(parseInt(price));
    }

    if (comparePriceEl) {
      if (comparePrice && parseInt(comparePrice) > parseInt(price)) {
        comparePriceEl.textContent = this.#formatMoney(parseInt(comparePrice));
        comparePriceEl.style.display = '';

        if (discountEl) {
          const discount = parseInt(comparePrice) - parseInt(price);
          const discountPercent = Math.round((discount / parseInt(comparePrice)) * 100);
          const addButton = productCard.querySelector('[data-add-to-cart]');
          const template = addButton?.dataset.discountTemplate || '__PERCENT__%';
          discountEl.textContent = template.replace('__PERCENT__', discountPercent);
          discountEl.style.display = '';
        }
      } else {
        comparePriceEl.style.display = 'none';
        if (discountEl) {
          discountEl.style.display = 'none';
        }
      }
    }
  }

  #formatMoney(cents) {
    const amount = (cents / 100).toFixed(2).replace('.', ',');
    return amount.replace(/,00$/, ',-').replace(/,(\d)0$/, ',$1');
  }

  async #addToCart(variantId, button) {
    button.classList.add('cart-upsells__add-button--loading');
    const originalText = button.textContent;
    button.disabled = true;

    try {
      const cartItemsComponents = document.querySelectorAll('cart-items-component');
      const sectionIds = [];
      cartItemsComponents.forEach((item) => {
        if (item.dataset.sectionId) {
          sectionIds.push(item.dataset.sectionId);
        }
      });

      const formData = new FormData();
      formData.append('id', variantId);
      formData.append('quantity', '1');
      if (sectionIds.length > 0) {
        formData.append('sections', sectionIds.join(','));
      }

      const response = await fetch(window.Theme?.routes?.cart_add_url || '/cart/add.js', {
        method: 'POST',
        body: formData,
        headers: {
          'Accept': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to add to cart');
      }

      const responseData = await response.json();

      document.dispatchEvent(
        new CustomEvent('cart:update', {
          bubbles: true,
          detail: {
            resource: responseData,
            sourceId: 'cart-upsells',
            data: {
              source: 'cart-upsells',
              itemCount: 1,
              sections: responseData.sections,
            },
          },
        })
      );

      button.textContent = button.dataset.textAdded || window.Theme?.translations?.added || 'Added';

      setTimeout(() => {
        button.textContent = originalText;
        button.classList.remove('cart-upsells__add-button--loading');
        button.disabled = false;
      }, 1500);
    } catch (error) {
      console.error('Error adding to cart:', error);
      button.classList.remove('cart-upsells__add-button--loading');
      button.textContent = originalText;
      button.disabled = false;
    }
  }
}

if (!customElements.get('cart-upsells-component')) {
  customElements.define('cart-upsells-component', CartUpsells);
}
